"use client";
import { useState, useCallback, useEffect, RefObject } from "react";

export const useAutoClose = (
    initialState = false,
    componentRef: RefObject<HTMLElement | null> | null = null,
): [boolean, React.Dispatch<React.SetStateAction<boolean>>, () => void] => {
    const [isOpen, setIsOpen] = useState(initialState);

    const close = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleDocumentClick = useCallback(
        (event: MouseEvent) => {
            if (
                isOpen &&
                event.target &&
                componentRef &&
                componentRef.current
            ) {
                // Check if the click is outside the component
                if (!componentRef.current.contains(event.target as Node)) {
                    setIsOpen(false);
                }
            }
        },
        [isOpen, componentRef],
    );

    const handleEscapeKey = useCallback((event: KeyboardEvent) => {
        if (event.key === "Escape") {
            setIsOpen(false);
        }
    }, []);

    useEffect(() => {
        if (componentRef) {
            document.addEventListener("click", handleDocumentClick);
            document.addEventListener("keydown", handleEscapeKey);
        }

        return () => {
            if (componentRef) {
                document.removeEventListener("click", handleDocumentClick);
                document.removeEventListener("keydown", handleEscapeKey);
            }
        };
    }, [handleDocumentClick, handleEscapeKey, componentRef]);

    return [isOpen, setIsOpen, close];
};
